'use client';

/**
 * PageTemplate Component
 *
 * Declarative, variant-based page wrapper that composes PortalLayout +
 * renders a large in-content title block (PageContentHeader) instead of
 * pushing title info into the AppNavBar.
 *
 * Pages declare their title, description, icon, badges, and actions via
 * props — the template renders them in the content area (Asana-style).
 *
 * Section tabs from nav-config are consumed via SectionTabsContext.
 * Page-level tabs override nav-config tabs when provided.
 *
 * @see Issue #330 - Unified Navigation System
 * @see PR #856 — Navigation & Layout Redesign
 */

import type { SvgIconProps } from '@mui/material';
import { Box } from '@mui/material';
import type { NavTab, SidebarConfig, VisibilityContext } from '@ops-matrix/shared-types';
import React, { useMemo } from 'react';
import { useSectionTabs } from '../../contexts/SectionTabsContext';
import { useI18n } from '../../hooks/useI18n';
import { PageContentHeader } from '../PageContentHeader/PageContentHeader';
import { PortalLayout } from '../PortalLayout/PortalLayout';

// ============================================================================
// Types
// ============================================================================

export type PageVariant = 'list' | 'detail' | 'form' | 'dashboard' | 'settings';

export interface PageTemplateProps {
  // --- Page Header ---
  /** i18n key for the page title (takes precedence over `title`) */
  titleKey?: string;
  /** Raw string title for dynamic content */
  title?: string;
  /** i18n key for the page description */
  descriptionKey?: string;
  /** Page description — string or rich ReactNode (chips, styled spans, links). */
  description?: React.ReactNode;
  /** Icon component displayed in the page header */
  icon?: React.ComponentType<SvgIconProps>;
  /** Badge/chip elements displayed below the title */
  badges?: React.ReactNode;
  /** Action buttons displayed in the header right section */
  actions?: React.ReactNode;
  /** Back navigation handler — shows a back arrow in the header when set */
  onBack?: () => void;

  // --- Inline Actions (Proposal 5) ---
  /** Favorite toggle handler */
  onFavorite?: () => void;
  /** Whether the page is currently favorited */
  isFavorited?: boolean;
  /** Status chip rendered inline after the title */
  statusChip?: React.ReactNode;
  /** Dropdown menu items for the chevron button next to title */
  titleMenuItems?: React.ReactNode;

  // --- Tab Bar (Proposal 3) ---
  /** Page-level tabs (override nav-config section tabs when provided) */
  tabs?: NavTab[];
  /** Currently active tab ID */
  activeTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tabId: string, path: string) => void;

  /**
   * Optional per-page chrome rendered inside the sticky `PageContentHeader`
   * between the description/badges row and the tabs (e.g. stage indicator,
   * progress strip). Pages that need a permanently-visible secondary row
   * should pass it here rather than rendering it inline in their content.
   */
  subHeader?: React.ReactNode;

  // --- Layout Control ---
  /** Page variant controlling default padding (default: 'list') */
  variant?: PageVariant;
  /** Maximum content width in px or MUI breakpoint (default: variant-specific, e.g. 1200 for settings) */
  maxWidth?: number | 'sm' | 'md' | 'lg' | 'xl' | false;
  /** Skip variant padding entirely (default: false) */
  noPadding?: boolean;

  // --- PortalLayout Config (passed by portal wrapper) ---
  /** Current URL pathname */
  pathname: string;
  /** Navigation callback (framework-agnostic) */
  onNavigate: (path: string) => void;
  /**
   * Optional hover prefetch hook forwarded to the sidebar rail.
   * Portals wire this to `router.prefetch` (Next.js) to warm the next
   * route's JS + data before the click lands.
   */
  onHoverPath?: (path: string) => void;
  /** Sidebar navigation configuration */
  navConfig: SidebarConfig;
  /** Visibility context for filtering nav items and tabs */
  visibilityContext: VisibilityContext;
  /**
   * Dynamic badge counts per nav-item id. Portal wrappers populate this from
   * live data (e.g. pending signatures count). Falls back to the static
   * `badge.count` on the NavItem when not provided.
   */
  badgeCounts?: Record<string, number>;
  /** Whether an impersonation banner is currently shown */
  impersonating?: boolean;
  /** Height of the impersonation banner in pixels */
  bannerHeight?: number;
  /** Render the header bar */
  renderHeader: (props: {
    showMenuButton: boolean;
    onMenuClick: () => void;
    portalName?: string;
    onLogoClick?: () => void;
    hideUserAvatar?: boolean;
  }) => React.ReactNode;
  /** Optional breadcrumbs slot */
  renderBreadcrumbs?: () => React.ReactNode;
  /** Optional banner slot (e.g., impersonation banner) */
  renderBanner?: () => React.ReactNode;
  /** Handler for logo/header click in sidebar */
  onLogoClick?: () => void;

  // --- User (for sidebar rail avatar) ---
  /** Current user for sidebar rail avatar */
  user?: { name?: string; email?: string; avatarUrl?: string };
  /** Handler for logout (from rail avatar menu) */
  onLogout?: () => void;
  /** Handler for profile navigation (from rail avatar menu) */
  onProfile?: () => void;
  /** Handler for Control Center navigation (from rail avatar menu) */
  onControlCenter?: () => void;
  /** i18n key for the settings menu item label in rail avatar menu (default: 'navigation.settings') */
  settingsMenuLabel?: string;
  /** Whether dark mode is active (for theme toggle in rail avatar menu) */
  isDarkMode?: boolean;
  /** Handler for theme toggle (from rail avatar menu) */
  onThemeToggle?: () => void;

  // --- Content ---
  /** Page content */
  children: React.ReactNode;
}

// ============================================================================
// Variant Defaults
// ============================================================================

// Variant paddings: horizontal + top only — bottom is intentionally 0 so content
// runs flush to the bottom of the scroll area (no awkward gap below tables, etc.).
// Horizontal padding is 10px (spacing 1.25) across all variants.
export const VARIANT_DEFAULTS: Record<PageVariant, { padding: Record<string, unknown>; maxWidth?: number }> = {
  list: { padding: { px: 1.25, pt: 0, pb: 0 } },
  detail: { padding: { px: 1.25, pt: 1.25, pb: 0 } },
  form: { padding: { px: 1.25, pt: 1.25, pb: 0 } },
  dashboard: { padding: { px: 1.25, pt: 1.25, pb: 0 } },
  settings: { padding: { px: 1.25, pt: 1.25, pb: 0 }, maxWidth: 1200 },
};

// ============================================================================
// Component
// ============================================================================

export const PageTemplate: React.FC<PageTemplateProps> = ({
  // Page Header
  titleKey,
  title,
  descriptionKey,
  description,
  icon,
  badges,
  actions,
  onBack,

  // Inline Actions
  onFavorite,
  isFavorited,
  statusChip,
  titleMenuItems,

  // Tab Bar
  tabs: pageTabs,
  activeTab: pageActiveTab,
  onTabChange: pageOnTabChange,

  // Sub-header slot (renders inside PageContentHeader, above tabs)
  subHeader,

  // Layout Control
  variant = 'list',
  maxWidth,
  noPadding = false,

  // PortalLayout Config
  pathname,
  onNavigate,
  onHoverPath,
  navConfig,
  visibilityContext,
  badgeCounts,
  impersonating,
  bannerHeight,
  renderHeader,
  renderBreadcrumbs,
  renderBanner,
  onLogoClick,

  // User (rail avatar)
  user,
  onLogout,
  onProfile,
  onControlCenter,
  settingsMenuLabel,
  isDarkMode,
  onThemeToggle,

  // Content
  children,
}) => {
  const { t } = useI18n();

  // Consume nav-config section tabs from SectionTabsContext
  const sectionTabs = useSectionTabs();

  // Resolve i18n title/description — keys take precedence over raw strings
  const resolvedTitle = useMemo(() => {
    if (titleKey) return t(titleKey);
    return title;
  }, [titleKey, title, t]);

  const resolvedDescription = useMemo(() => {
    if (descriptionKey) return t(descriptionKey);
    return description;
  }, [descriptionKey, description, t]);

  // Resolve tabs: page-level tabs override nav-config section tabs
  const resolvedTabs = pageTabs ?? sectionTabs?.tabs;
  const resolvedActiveTab = pageActiveTab ?? sectionTabs?.activeTab;
  const resolvedOnTabChange = pageOnTabChange ?? sectionTabs?.onTabChange;
  const resolvedTabVisibility = visibilityContext ?? sectionTabs?.visibilityContext;

  // Get variant defaults
  const variantDefaults = VARIANT_DEFAULTS[variant];
  const resolvedMaxWidth = maxWidth !== undefined ? maxWidth : variantDefaults.maxWidth;

  // When the top-level child is a SplitPanel, skip outer padding so the side
  // panel sits flush to the viewport edge. SplitPanel applies equivalent
  // padding to its main-content area internally so the list still has
  // breathing room.
  // We match on `displayName === 'SplitPanel'` rather than component identity
  // so this survives module duplication between source and built bundles.
  const hasSplitPanelChild = useMemo(
    () =>
      React.Children.toArray(children).some((child) => {
        if (!React.isValidElement(child)) return false;
        const type = child.type as { displayName?: string; name?: string } | string;
        if (typeof type === 'string') return false;
        return type.displayName === 'SplitPanel' || type.name === 'SplitPanel';
      }),
    [children],
  );

  // Build content styles: variant padding + flex-aware scrolling container.
  // PageContentHeader stays fixed (not in scroll area); content scrolls independently below it.
  // SplitPanel needs the parent to be a flex column so the panel can `flex: 1`
  // to fill the remaining viewport height. Block-level children (Box, GlassCard,
  // Grid, tables) don't participate in flex sizing, and on those pages flex
  // column on the parent breaks overflow:auto so scroll silently fails. Apply
  // flex layout only when a SplitPanel needs it.
  const contentSx = {
    ...(noPadding || hasSplitPanelChild ? {} : variantDefaults.padding),
    ...(resolvedMaxWidth ? { maxWidth: resolvedMaxWidth, mx: 'auto', width: '100%' } : {}),
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    ...(hasSplitPanelChild ? { display: 'flex', flexDirection: 'column' } : {}),
  };

  const hasHeader = resolvedTitle || icon || resolvedDescription;

  return (
    <PortalLayout
      pathname={pathname}
      onNavigate={onNavigate}
      onHoverPath={onHoverPath}
      navConfig={navConfig}
      visibilityContext={visibilityContext}
      badgeCounts={badgeCounts}
      impersonating={impersonating}
      bannerHeight={bannerHeight}
      renderHeader={renderHeader}
      renderBreadcrumbs={renderBreadcrumbs}
      renderBanner={renderBanner}
      onLogoClick={onLogoClick}
      user={user}
      onLogout={onLogout}
      onProfile={onProfile}
      onControlCenter={onControlCenter}
      settingsMenuLabel={settingsMenuLabel}
      isDarkMode={isDarkMode}
      onThemeToggle={onThemeToggle}
    >
      {/* PageContentHeader — fixed at top, never scrolls */}
      {hasHeader && (
        <PageContentHeader
          title={resolvedTitle}
          description={resolvedDescription}
          icon={icon}
          onBack={onBack}
          onFavorite={onFavorite}
          isFavorited={isFavorited}
          statusChip={statusChip}
          titleMenuItems={titleMenuItems}
          badges={badges}
          actions={actions}
          tabs={resolvedTabs}
          activeTab={resolvedActiveTab}
          onTabChange={resolvedOnTabChange}
          visibilityContext={resolvedTabVisibility}
          subHeader={subHeader}
        />
      )}
      {/* Content area — scrolls independently, SplitPanel fills via flex */}
      <Box component="main" sx={contentSx}>
        {children}
      </Box>
    </PortalLayout>
  );
};

export default PageTemplate;
